import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Loader2, AlertCircle, Activity, Clock } from "lucide-react";

import { formatDisplayDate, formatDisplayTime } from "./utils/formatters";
import { offsetRingNo, getRingNumeric, inRingOrder } from "./utils/helpers";

import OverviewView from "./components/views/OverviewView";
import GroutRecordView from "./components/views/GroutRecordView";
import SegmentRecordView from "./components/views/SegmentRecordView";
import GroutDashboardView from "./components/views/GroutDashboardView";
import SegmentDashboardView from "./components/views/SegmentDashboardView";
import ExecutiveDashboardView from "./components/views/ExecutiveDashboardView";
import ReportView from "./components/views/ReportView";
import ShiftReportView from "./components/views/ShiftReportView";
import SegmentAnalysisView from "./components/views/SegmentAnalysisView";
import GroutAnalysisView from "./components/views/GroutAnalysisView";
import RouteScheduleView from "./components/views/RouteScheduleView";
import HeadLevelView from "./components/views/HeadLevelView";
import PerformanceView from "./components/views/PerformanceView";
import DailyReportView from "./components/views/DailyReportView";
import RecordDailyView from "./components/views/RecordDailyView";
import PrepGanttView from "./components/views/PrepGanttView";
import InstrumentDashboardView from "./components/views/InstrumentDashboardView";
import InstrumentLocationView from "./components/views/InstrumentLocationView";
import InstrumentScheduleView from "./components/views/InstrumentScheduleView";
import { makeInstId } from "./utils/instruments";
import { markMeasurementDone, markMeasurementNA, cancelMeasurement } from "./utils/instrumentSchedule";
import { useFilterState } from "./hooks/useGlobalFilter";
import { upsertIssue, setIssueStatus, removeIssue, forMachine } from "./utils/issues";
import { upsertDailyReport, removeDailyReport } from "./utils/dailyReports";
import { getMachineConfig } from "./utils/machineConfig";
import { isViewerMode, VIEWER_TABS } from "./utils/viewerMode";
import { useOfflineData } from "./offline/useOfflineData";
import { applyServerRows } from "./offline/serverDeletions";
import { businessEnvelope } from "./offline/businessWrites";
import { stuckCount, travellingCount } from "./offline/syncSummary";
import { useOfflineControls } from "./components/offline/OfflineControls";
import { useOffline } from "./offline/OfflineProvider";
import { toSyncVersion } from "./offline/syncVersion";
import { applyOptimisticRow, revertOptimisticRow, stripQueuedPhotos } from "./offline/displayRecord";
import { writeThrough } from "./offline/writeThrough";

import { Shell, NAV_GROUPS } from "./ui-ux-pro-max";
import "./styles/globals.css";

// stable empty array so the machine-switch gate does not remount every consumer each render
const EMPTY_ROWS = [];
const EMPTY_SYNC_META = {};

// Families whose durable on-screen copy is App's own state, updated by the handler before the write
// is queued — so there is nothing for `applyOptimisticRecord` to do and nothing wrong.
//
// `prepTask` and the three configs are NOT in here, and the difference is where the state lives. A
// view's own `useState` dies on unmount, and App's copy of these is only ever set from
// `offlineData`, which re-reads on mount, machine change or an explicit refresh — none of which a
// queued write triggers. Left exempt, a prep task added offline was queued, shown, and gone the
// moment the crew opened another page, with the warning that exists to catch exactly that silenced.
const SELF_RENDERED_ENTITY_TYPES = new Set([
  "issue", "dailyReport", "instrument", "instReading", "instSchedule",
]);

const PrimaryGroutApp = () => {
  const [currentModule, setCurrentModule] = useState("segment");
  const [activeTab, setActiveTab] = useState(() => (isViewerMode() ? "dashboard" : "overview"));
  const [isLoadingMain, setIsLoadingMain] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [projectInfo, setProjectInfo] = useState({ date: new Date().toISOString().split("T")[0], shift: "Day", location: "อุโมงค์จากบ่อ IS4 ถึง บ่อ IS2", tbmNo: "TBM1" });
  const [activeMachine, setActiveMachine] = useState(() => localStorage.getItem("tbmActiveMachine") || "TBM1");
  const isViewer = isViewerMode();
  useEffect(() => { try { localStorage.setItem("tbmActiveMachine", activeMachine); } catch (e) {} }, [activeMachine]);
  useEffect(() => { setProjectInfo((p) => ({ ...p, ...getMachineConfig(activeMachine) })); }, [activeMachine]);
  const [groutRecords, setGroutRecords] = useState([]);
  const [secondaryGroutRecords, setSecondaryGroutRecords] = useState([]);
  const [segmentRecords, setSegmentRecords] = useState([]);
  const [shiftReports, setShiftReports] = useState([]);
  // which machine the machine-scoped rows above currently hold (null until the first snapshot lands)
  const [rowsMachine, setRowsMachine] = useState(null);
  const [machineProgress, setMachineProgress] = useState(null);
  const [routeProjectTotal, setRouteProjectTotal] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);

  const [instLocations, setInstLocations] = useState([]);
  const [instInstruments, setInstInstruments] = useState([]);
  const [instThresholds, setInstThresholds] = useState([]);
  const [instReadings, setInstReadings] = useState([]);
  const [instSchedules, setInstSchedules] = useState([]);
  const [selectedInstLocId, setSelectedInstLocId] = useState(null);

  const [issues, setIssues] = useState([]);
  useEffect(() => {
    if (isViewer && !VIEWER_TABS.includes(activeTab)) setActiveTab("dashboard");
  }, [isViewer, activeTab]);

  const [dailyReports, setDailyReports] = useState([]);
  const [pendingRecordForm, setPendingRecordForm] = useState(null);
  // Per machine, from the snapshot. It used to live in one localStorage key for both machines, so
  // whichever machine was active last wrote over the other one's plan.
  // Keyed by machine, like `routeConfigs`. `planConfig_<machine>` and `distPlanConfig_<machine>` are
  // separate rows on the sheet, and TBM2 has neither — so one untagged slot meant a switch to TBM2
  // kept TBM1's plan on screen, fed it to the executive plan-variance figure and the plan lines, and
  // wrote it to TBM2's own row the moment anyone pressed save.
  const [planConfigs, setPlanConfigs] = useState({});
  const [distPlanConfigs, setDistPlanConfigs] = useState({});
  const [routeConfigs, setRouteConfigs] = useState(null);
  // Project-wide, like the payload that carries it: one list holds both machines' rows, and the
  // view is handed the slice for the machine on screen.
  const [prepTasks, setPrepTasks] = useState([]);
  const handleSaveDailyReport = (report) => {
    const next = upsertDailyReport(dailyReports, report);
    setDailyReports(next);
    const saved = report.id ? next.find((r) => r.id === report.id) : next.find((r) => !dailyReports.some((o) => o.id === r.id));
    if (saved) queueRecord("dailyReport", report.id ? "update" : "create", { ...saved, machine: saved.machine || activeMachine }, saved.machine || activeMachine);
  };
  const handleDeleteDailyReport = (id) => {
    // the record, not `{ id }`: the domain key is derived from the payload's own machine, and a
    // delete keyed differently from the record it removes queues against a domain nothing else touches
    const removed = dailyReports.find((r) => r.id === id);
    const next = removeDailyReport(dailyReports, id); setDailyReports(next);
    if (removed) queueRecord("dailyReport", "delete", removed, removed.machine || activeMachine);
  };

  // The five families whose on-screen copy is their own state, changed by the handler before the
  // write is sent — the same five as `SELF_RENDERED_ENTITY_TYPES`, which is what tells
  // `applyOptimisticRecord` to leave them alone. Rebuilt each render, which is the point: the `rows`
  // a handler sees here are the ones from before it called its setter.
  const SELF_RENDERED_STATE = {
    issue: { rows: issues, set: setIssues },
    dailyReport: { rows: dailyReports, set: setDailyReports },
    instrument: { rows: instInstruments, set: setInstInstruments },
    instReading: { rows: instReadings, set: setInstReadings },
    instSchedule: { rows: instSchedules, set: setInstSchedules },
  };
  if (process.env.NODE_ENV !== "production") {
    // A family added to the Set but not here would keep a refused write on screen with nothing to
    // undo it — the failure this whole pass exists to close.
    SELF_RENDERED_ENTITY_TYPES.forEach((entityType) => {
      if (!SELF_RENDERED_STATE[entityType]) console.warn(`${entityType} renders from its own state but has no slot to revert a refused write`);
    });
  }

  // Every business write App owns. `makeDomainKey` decides whether the key carries a machine or
  // GLOBAL — the caller does not, which is why `machine` is passed through untouched and only the
  // machine-keyed families supply one. The `.catch(console.warn)` these replaced meant a failed
  // write was a line in a console nobody reads.
  // The promise is CAUGHT, not dropped. `repository.mutate` rejects on the states `db.js` names by
  // hand — an upgrade blocked by another tab after a service-worker update, a quota the device has
  // run out of — and on a record the envelope cannot identify. Until Task 9 a rejection here still
  // left the row in localStorage; now the queue is the only durable copy, so a silent rejection is
  // a record that exists nowhere while the screen shows it saved. The record views already say this
  // out loud on the identical failure.
  // `machine` defaults to the one on screen. For a project-wide family that is a SCOPE HINT, not a
  // tag — `makeDomainKey` ignores it and the key stays GLOBAL — and without it a write made before
  // this device ever fetched has no snapshot to be patched into and is on no screen after a
  // relaunch. The record's own `machine` column is untouched: `optimisticEntity` prefers it.
  const queueRecord = (entityType, operation, record, machine = activeMachine) => {
    // Read at RENDER time, so this is the list as it was BEFORE the handler's own setState — the
    // handlers all change their list and then call this, in one render. Captured here rather than at
    // each of the nine call sites on purpose: the same rule spread across call sites is how this
    // branch has produced defect after defect, one site updated out of two.
    const slot = SELF_RENDERED_STATE[entityType];
    const previousIndex = slot && record ? slot.rows.findIndex((row) => String(row && row.id) === String(record.id)) : -1;
    const previous = previousIndex === -1 ? null : slot.rows[previousIndex];
    return mutateBusinessRecord(businessEnvelope({ entityType, operation, record, machine, syncMeta }))
      // Write-through: this rejects when the SERVER did not take the write, not only when the local
      // queue refused it. These families change their own state BEFORE the write, so a refusal leaves
      // the screen ahead of the sheet — put that one row back the way it was, and say what happened.
      // The families App does not hold (`segment`, `grout`, the configs, prep tasks) never got their
      // row applied at all: `mutateBusinessRecord` places it only on success.
      .catch((error) => {
        if (slot) slot.set((rows) => revertOptimisticRow(rows, record && record.id, previous, previousIndex));
        alert("บันทึกไม่สำเร็จ — ยังไม่ขึ้นเซิร์ฟเวอร์: " + (error && error.message ? error.message : error) + (slot ? "\nหน้าจอย้อนกลับให้แล้ว ลองทำรายการนี้ใหม่อีกครั้ง" : ""));
      });
  };
  const queueIssue = (record, operation) => queueRecord("issue", operation, record);

  const handleSaveIssue = (form) => {
    const next = upsertIssue(issues, form);
    setIssues(next);
    const saved = form.id ? next.find((i) => i.id === form.id) : next[0];
    if (saved) queueIssue(saved, form.id ? "update" : "create");
  };
  const handleSetIssueStatus = (id, status) => {
    const next = setIssueStatus(issues, id, status);
    setIssues(next);
    const changed = next.find((i) => i.id === id);
    if (changed) queueIssue(changed, "update");
  };
  const handleDeleteIssue = (id) => {
    const removed = issues.find((i) => i.id === id);
    setIssues(removeIssue(issues, id));
    if (removed) queueIssue(removed, "delete");
  };

  const handleSaveInstReading = (reading) => {
    const row = reading.id ? reading : { ...reading, id: makeInstId("rd"), enteredBy: "manual" };
    const next = reading.id ? instReadings.map((r) => (r.id === row.id ? row : r)) : [row, ...instReadings];
    setInstReadings(next);
    queueRecord("instReading", reading.id ? "update" : "create", row);
  };
  // kind: "done" | "na" | "cancel" — default "done" เพื่อไม่ break v1 schedule view ที่เรียก
  // onMark({ ...s, isMeasured:true, measuredAt: today() }) แบบ 1 argument (toggle done เดิม)
  // R3 จะเพิ่ม modal เลือก done/NA/date เอง แล้วส่ง kind + measuredAtISO มาตรงๆ
  const handleMarkInstSchedule = (sched, kind = "done", measuredAtISO) => {
    const { next, changed } =
      kind === "na" ? markMeasurementNA(instSchedules, sched.id) :
      kind === "cancel" ? cancelMeasurement(instSchedules, sched.id) :
      markMeasurementDone(instSchedules, sched.id, measuredAtISO ?? sched.measuredAt);
    setInstSchedules(next);
    // one mutation per schedule row, not one batch: marking a measurement can move several rows at
    // once, and the queue orders per record — a row the server refuses must strand only itself
    changed.forEach((row) => queueRecord("instSchedule", "update", row));
  };
  const handleUpdateInstrument = (ins) => {
    const next = instInstruments.map((i) => (i.id === ins.id ? ins : i));
    setInstInstruments(next);
    queueRecord("instrument", "update", ins);
  };

  // Offline-first hydration: the cached snapshot renders immediately, then the server refresh
  // replaces it. The repository owns the fetch, the race guard and the stale flag, so this effect
  // only mirrors `data` into the existing state contracts (write migration lands in Tasks 8-9).
  const offlineData = useOfflineData(activeMachine);
  // the sync status button and the panels behind it, sharing one open/close state
  const offlineControls = useOfflineControls();
  const { repository, runner, syncSummary } = useOffline();

  useEffect(() => {
    const data = offlineData.data;
    if (!data || data.machine !== activeMachine) return;
    // Photo bytes are stripped here as well as on the queued write. A record whose photo has not
    // synced yet is stored WITH its base64 — the queue still has to send it — and `readServerSnapshot`
    // hands that payload straight back, so a relaunch put the bytes into React state through this
    // mirror even though the write path was careful not to.
    setSegmentRecords(data.segments.map(stripQueuedPhotos));
    setGroutRecords(data.grouts.map(stripQueuedPhotos));
    // F1: secondary grout (dataset แยก) — normalizer parse positions เหมือน primary, ไม่มี ratio
    setSecondaryGroutRecords(data.secondaryGrouts.map(stripQueuedPhotos));
    setShiftReports(data.shiftReports);
    // the rows on screen now belong to this machine (see rowsReady below)
    setRowsMachine(activeMachine);
    // Task 9 Step 5 retired localStorage for these collections, and that changed what this block
    // has to be. The server-only rule existed because a cached read could put a snapshot into state
    // over a record that lived only in localStorage. Writes go through the queue now and the
    // snapshot re-injects whatever has not synced, so the cached read CARRIES the crew's offline
    // records instead of destroying them — and since nothing seeds these lists at mount any more,
    // keeping the rule would have meant an offline launch showing none of them. For the route and
    // plan configs it would have been worse than blank: `routeConfigFor` falls back to
    // `DEFAULT_ROUTE_LEGS`, so the crew would have been shown factory distances in place of the
    // route they saved.
    //   The `.length` guards stay, for a different reason: `normalizeServerData` maps an absent key
    // to [], and GAS's own `getSheetDataAsJson` returns [] for a sheet that does not exist, so an
    // empty list cannot mean "deleted". What removes a record is the server naming it —
    // `applyServerRows` and the SyncMeta tombstone behind it (Step 5b, `serverDeletions.js`).
    // Step 5b: an empty collection removes only what the server has tombstoned. `applyServerRows`
    // carries the whole rule and the reason it cannot be emptiness alone.
    const serverMeta = data.syncMeta;
    setIssues((previous) => applyServerRows("issue", data.issues, previous, serverMeta, activeMachine));
    setDailyReports((previous) => applyServerRows("dailyReport", data.dailyReports, previous, serverMeta, activeMachine));
    if (data.prepTasks.length) {
      // A machine absent from the payload keeps its own rows — the rule the per-machine localStorage
      // keys used to give for free — and a tombstone still outranks it, because a payload naming
      // only TBM1 can still say TBM2's last task was deleted.
      //
      // The stored snapshot does NOT apply this carve-out: `writeServerSnapshot` replaces a
      // non-empty collection wholesale, so the row this keeps on screen is gone from the cache and
      // disappears at the next relaunch with nothing said. Keeping it here is still the right
      // direction — a partial `doGet` must not empty a machine's Work Plan — and closing the gap
      // means teaching the write path the same carve-out, which is Task 10's work on a rule that
      // currently has to be spelled twice to exist at all. Recorded in open item 3v.
      const carried = new Set(data.prepTasks.map((t) => t.machine || "TBM1"));
      setPrepTasks((previous) => [
        ...applyServerRows("prepTask", [], previous.filter((t) => !carried.has(t.machine || "TBM1")), serverMeta, activeMachine),
        ...data.prepTasks,
      ]);
    } else {
      setPrepTasks((previous) => applyServerRows("prepTask", [], previous, serverMeta, activeMachine));
    }

    // Configs are singletons: there is no collection to empty, so no tombstone question. A null is
    // "this response did not carry one", which must not blank a config the crew is looking at.
    //   A machine whose config was written before it ever loaded has a snapshot holding that config
    // and nothing else, so `load` reports `indexeddb` and the app says "showing saved data" over
    // empty lists. The lists ARE empty and the config IS saved, so the notice is not false — but it
    // reads as reassurance. Recorded rather than fixed (open item 3t): telling the two apart needs a
    // "never fetched" flag on the snapshot, which is Task 10's status work.
    // Still guarded: a response that did not carry this machine's config must not blank what the
    // crew is looking at. Now the slot it does not blank is this machine's own.
    if (data.planConfig) setPlanConfigs((previous) => ({ ...previous, [activeMachine]: data.planConfig }));
    if (data.distPlanConfig) setDistPlanConfigs((previous) => ({ ...previous, [activeMachine]: data.distPlanConfig }));
    // both machines' route configs — the distance table shows them side by side
    if (data.routeConfigs && typeof data.routeConfigs === "object") setRouteConfigs(data.routeConfigs);
    // `machineProgress` is exempt from Step 5b by construction: GAS computes it on every response
    // rather than reading a sheet, so it has no records, no deletions and nothing to tombstone.
    if (data.machineProgress) setMachineProgress(data.machineProgress);
    if (data.routeProjectTotal != null) setRouteProjectTotal(data.routeProjectTotal);
    // Instrument module: project-wide (ไม่ขึ้นกับ activeMachine). Server-only, same rule as above.
    // instLocation/instThreshold have no sync entity, so they have no tombstone to read and an
    // empty response leaves them alone — the same conservative end the guard always had.
    const mirrorInst = (entityType, rows, setter) =>
      setter((previous) => applyServerRows(entityType, rows, previous, data.syncMeta, activeMachine));
    mirrorInst("instLocation", data.instLocations, setInstLocations);
    mirrorInst("instrument", data.instInstruments, setInstInstruments);
    mirrorInst("instThreshold", data.instThresholds, setInstThresholds);
    mirrorInst("instReading", data.instReadings, setInstReadings);
    mirrorInst("instSchedule", data.instSchedules, setInstSchedules);
  }, [offlineData.data, offlineData.source, activeMachine]);

  useEffect(() => {
    setIsLoadingMain(offlineData.loading);
    // Only block the app with an error when there is nothing to show. With cached data on screen a
    // failed refresh is the normal offline case and must not raise a full-width red banner.
    const hasData = offlineData.source !== "empty";
    setLoadError(offlineData.error && !hasData ? "ไม่สามารถดึงข้อมูลได้: " + offlineData.error.message : null);
  }, [offlineData.loading, offlineData.error, offlineData.source]);

  const handleProjectInfoChange = (e) => setProjectInfo({ ...projectInfo, [e.target.name]: e.target.value });

  const handleNavigate = (item) => {
    setActiveTab(item.tab);
    if (item.module) setCurrentModule(item.module);
    setMoreOpen(false);
  };

  // Machine-scoped rows are only exposed once they belong to the selected machine. Between a switch
  // and the new machine's snapshot resolving, the state still holds the previous machine's rows;
  // showing those under the new machine's label also let the record form prefill the next ring
  // number and chainage from the wrong machine's last ring.
  const rowsReady = rowsMachine === activeMachine;
  // A record form can unmount while its save is still in flight — any nav tap does it, and the
  // machine switcher lives in the TopBar, so it is reachable from every tab. A ref inside the form
  // freezes at unmount and would answer with the machine that was selected then. App never
  // unmounts, so the live answer has to come from here.
  const activeMachineRef = useRef(activeMachine);
  activeMachineRef.current = activeMachine;
  const isCurrentMachine = useCallback((m) => activeMachineRef.current === m, []);
  // In ring order, here and only here: every view reads the last row as the latest ring, and a ring
  // recorded late is appended below rings it comes before (see `inRingOrder`).
  const activeSegments     = useMemo(() => (rowsReady ? inRingOrder(segmentRecords) : EMPTY_ROWS), [rowsReady, segmentRecords]);
  const currentRingNum = activeSegments.reduce((mx, s) => Math.max(mx, getRingNumeric(s.ringNo) || 0), 0);
  const activeGrouts       = rowsReady ? groutRecords : EMPTY_ROWS;
  const activeSecondaryGrouts = rowsReady ? secondaryGroutRecords : EMPTY_ROWS;
  const activeShiftReports = rowsReady ? shiftReports : EMPTY_ROWS;
  const activeDailyReports = dailyReports.filter((r) => (r.machine || "TBM1") === activeMachine);
  const activePrepTasks = useMemo(() => prepTasks.filter((t) => (t.machine || "TBM1") === activeMachine), [prepTasks, activeMachine]);
  const activeIssues = forMachine(issues, activeMachine);
  const dashFilter = useFilterState();

  // Versions confirmed since the last full snapshot, per domain key. Views read the merged map to
  // stamp `baseVersion`, which is what lets the server refuse an edit made against a row that has
  // since moved on. `data.syncMeta` only advances on a `getData`, so on its own it goes stale the
  // moment a mutation syncs — and a stale `baseVersion` is not a harmless nuisance: the server
  // rejects the next edit of that record as a conflict, and the conflict blocks its domain. These
  // land from the repository's own sync events and win over the snapshot, always the older of the
  // two. `toSyncVersion` because a version can come back through Sheets as text, which GAS itself
  // accepts — refusing it here would quietly send 0 and reopen the hole this map exists to close.
  const [confirmedVersions, setConfirmedVersions] = useState(EMPTY_SYNC_META);
  useEffect(() => repository.subscribe(event => {
    if (event.type !== "sync" || !event.domainKey || event.version == null) return;
    const version = toSyncVersion(event.version);
    // `deleted` travels with it. The next create on this key reads the flag to decide whether it is
    // claiming a tombstone or colliding with a live record, and an entry carrying only a version
    // reads as live — so a ring deleted and re-recorded in one session was refused, and the refusal
    // then parked at the head of that ring's domain where nothing behind it could be sent either.
    const entry = { version, deleted: Boolean(event.deleted) };
    setConfirmedVersions(prev => {
      const held = prev[event.domainKey];
      if (held && held.version === version && Boolean(held.deleted) === entry.deleted) return prev;
      return { ...prev, [event.domainKey]: entry };
    });
  }), [repository]);
  const snapshotSyncMeta = (offlineData.data && offlineData.data.syncMeta) || EMPTY_SYNC_META;
  // The NEWER of the two per key, not simply the local one. A confirmed version is newer than the
  // snapshot only until another device writes: once a second phone takes a ring to version 9, a
  // full refresh carries 9 while this device still remembers confirming 2, and preferring its own
  // memory would stamp 2 for the rest of the session. The server answers `conflict` for a row this
  // device merely read stale, and that conflict blocks the ring's domain — the same failure this
  // map exists to prevent, arriving from the other direction.
  const syncMeta = useMemo(() => {
    if (confirmedVersions === EMPTY_SYNC_META) return snapshotSyncMeta;
    const merged = { ...snapshotSyncMeta };
    Object.keys(confirmedVersions).forEach(key => {
      const confirmed = toSyncVersion(confirmedVersions[key] && confirmedVersions[key].version);
      const stored = toSyncVersion(merged[key] && merged[key].version);
      if (confirmed > stored) merged[key] = confirmedVersions[key];
    });
    return merged;
  }, [snapshotSyncMeta, confirmedVersions]);

  // The row the crew sees until the server confirms. It is written here rather than in the views so
  // that one writer owns each list — the machine guard below decides whether the row belongs on
  // screen at all, and a second writer could step past it without anything failing.
  const applyOptimisticRecord = useCallback((entityType, operation, record) => {
    if (!record) return;
    // A config is a singleton, not a row in a list, so it is mirrored by replacing the value rather
    // than by `applyOptimisticRow`. The payload carries the config under its own entity name, the
    // same shape `configValue` reads on the snapshot side.
    // Always the machine on screen in practice — a config envelope is built by the view showing it
    // — so this reads the record first and falls back rather than implying a difference the app can
    // produce. Nothing pins the distinction, because nothing can reach it.
    const configMachine = record.machine || activeMachineRef.current;
    if (entityType === "planConfig") return setPlanConfigs((previous) => ({ ...previous, [configMachine]: record.planConfig ?? record }));
    if (entityType === "distPlanConfig") return setDistPlanConfigs((previous) => ({ ...previous, [configMachine]: record.distPlanConfig ?? record }));
    if (entityType === "routeConfig") {
      setRouteConfigs((previous) => ({ ...(previous || {}), [configMachine]: record.routeConfig ?? record }));
      if (record.routeProjectTotal !== undefined) setRouteProjectTotal(record.routeProjectTotal);
      return;
    }
    const setter = {
      segment: setSegmentRecords,
      grout: setGroutRecords,
      secondaryGrout: setSecondaryGroutRecords,
      shiftReport: setShiftReports,
      prepTask: setPrepTasks,
    }[entityType];
    // Task 9 routes the remaining entities through this same call. One that arrives without a setter
    // queues correctly and then simply never appears, until a refresh — a save the crew watched
    // succeed and cannot see, which is the failure this whole task exists to prevent. Say so.
    if (!setter) {
      // The families App does not mirror keep their own state and have already updated it by the
      // time the write is queued — issues, daily reports, instruments, readings, schedules. Prep
      // tasks and the three configs cannot reach this branch at all: the first has a setter above,
      // the others return twenty lines earlier. Anything else arriving here queues correctly and
      // then never appears until a refresh, which is a save the crew watched succeed and cannot see.
      if (!SELF_RENDERED_ENTITY_TYPES.has(entityType) && process.env.NODE_ENV !== "production") {
        console.warn(`applyOptimisticRecord has no setter for ${entityType}; the queued write will not show until the next refresh`);
      }
      return;
    }
    setter(prev => applyOptimisticRow(prev, operation, record));
  }, []);

  // Every core engineering write goes through here — and since 2026-08-06 it is write-through:
  // `writeThrough` does not resolve until the sheet has the row, and a write the server never took is
  // discarded rather than kept for later. "Saved" means saved on the SHEET again, which is what the
  // crew reads it as. The queue's envelopes still carry `requestId` and `baseVersion`, so a re-press
  // after a dropped POST cannot double-write and an edit against a stale row is still refused.
  const mutateBusinessRecord = useCallback(async (input) => {
    const optimisticRecord = await writeThrough({ repository, runner, input });
    // Reached only on success now, so the row this places is one the sheet holds. A save still
    // resolves after the crew may have switched machine, and the machine-scoped arrays belong to
    // whichever machine is selected NOW — appending one machine's ring to the other's state is what
    // makes the record form derive the next ring from the wrong machine and the shift report send an
    // update carrying an id the other machine's sheet has never had. The write itself already
    // landed; only the on-screen copy is withheld, and the next snapshot restores it.
    // Every envelope carries a machine now — `queueRecord` defaults to the one on screen and the
    // views always pass theirs — so this is the machine test alone. The `!input.machine` disjunct
    // that stood here was dead the moment that default went in, and a dead branch in a guard is
    // worse than no branch: it sends the next reader somewhere nothing can execute.
    if (isCurrentMachine(input.machine)) applyOptimisticRecord(input.entityType, input.operation, optimisticRecord);
    return optimisticRecord;
  }, [applyOptimisticRecord, isCurrentMachine, repository, runner]);

  const liveHeaderStatus = useMemo(() => {
    if (activeSegments.length === 0) return null;
    const map = new Map();
    activeSegments.forEach(rec => map.set(rec.ringNo, rec));
    const deduped = Array.from(map.values());
    const lastSeg = deduped[deduped.length - 1];

    if (lastSeg.status === "In Progress") {
      if (lastSeg.excavStartTime && !lastSeg.excavEndTime) return { text: `กำลังขุดเจาะ ${lastSeg.ringNo}`, color: "bg-amber-500", icon: <AlertCircle size={12} /> };
      if (lastSeg.excavEndTime && !lastSeg.installStartTime) return { text: `ขุดเสร็จ รอประกอบ ${lastSeg.ringNo}`, color: "bg-slate-500", icon: <Clock size={12} /> };
      if (lastSeg.installStartTime && !lastSeg.installEndTime) return { text: `กำลังประกอบ ${lastSeg.ringNo}`, color: "bg-emerald-500", icon: <Activity size={12} /> };
      return { text: `กำลังทำงาน ${lastSeg.ringNo}`, color: "bg-blue-500", icon: <Activity size={12} /> };
    }
    return { text: `เครื่องจักรจอดพัก · รอ ring ${offsetRingNo(lastSeg.ringNo, 1)}`, color: "bg-slate-500", icon: <Clock size={12} /> };
  }, [activeSegments]);

  // Derive page title from NAV_GROUPS based on activeTab + currentModule
  const pageTitle = useMemo(() => {
    for (const group of NAV_GROUPS) {
      for (const item of group.items) {
        if (item.tab === activeTab && (item.module ? item.module === currentModule : !item.module || true)) {
          // Prefer exact module match when tab has module items
          if (item.module && item.module !== currentModule) continue;
          return item.label;
        }
      }
    }
    return `${activeMachine} Monitoring`;
  }, [activeTab, currentModule, activeMachine]);

  // Map liveHeaderStatus { text, color, icon } → Badge shape { code, label }
  const shellLiveStatus = useMemo(() => {
    if (!liveHeaderStatus) return null;
    const colorCodeMap = {
      "bg-amber-500": "b",
      "bg-slate-500": "neutral",
      "bg-emerald-500": "a",
      "bg-blue-500": "info",
    };
    return {
      code: colorCodeMap[liveHeaderStatus.color] || "neutral",
      label: liveHeaderStatus.text,
    };
  }, [liveHeaderStatus]);

  // Non-blocking snapshot state (design §7: keep the local snapshot visible and warn that it is
  // stale rather than blocking). Task 10 replaces this strip with the full Sync Center.
  const offlineNotice = useMemo(() => {
    // The queue's own state rides ALONGSIDE whatever else the strip is saying, because it is a
    // different fact about a different thing and both can be true at once. Making it a branch of
    // its own put it in front of everything below — including "บันทึกลงเครื่องไม่ได้", which says
    // the queue is not durable at all and outranks anything in it — and, being permanent until
    // Task 10 exists, it would have held that place for good. The quiet half had the mirror-image
    // fault: sitting last, it could never appear while the device was offline, which is the one
    // situation it was written for.
    // `blocked` counts what is queued behind a stuck head: never posted, so never "on its way"
    const stuck = stuckCount(syncSummary);
    const waiting = travellingCount(syncSummary);
    // Task 10's Sync Center is where a stuck write gets resolved, and the button that opens it is in
    // the bar beside this line — so this says where to go rather than telling the crew to call
    // someone. Its row stays on screen looking recorded while the sheet has never seen it, and the
    // queue orders per record, so everything they do to that ring waits behind it.
    // both, when both are true: they are different facts, and collapsing them understated how much
    // work is unsendable while a stuck head sits in front of it
    const queueNote = [
      // the viewer link has no status button (Shell hides it), so it is not told to open one
      stuck > 0 ? `${stuck} รายการต้องแก้ ยังไม่ขึ้นเซิร์ฟเวอร์${isViewer ? "" : " — เปิด “สถานะการซิงก์”"}` : null,
      waiting > 0 ? `${waiting} รายการรอซิงก์ขึ้นเซิร์ฟเวอร์` : null,
    ].filter(Boolean).join(" · ") || null;
    const withQueue = notice => (queueNote ? { ...notice, text: `${notice.text} · ${queueNote}` } : notice);
    // The rows for this machine are not on screen yet (first launch, or a machine switch whose
    // snapshot is still loading). Step 4 requires a `refreshing` signal here: the lists are empty
    // because we refuse to show another machine's rings, so say so rather than look like no data.
    if (!rowsReady) return withQueue({ text: "กำลังโหลดข้อมูลของเครื่องนี้…", spinning: true });
    if (offlineData.refreshing) return withQueue({ text: "กำลังอัปเดตข้อมูลจากเซิร์ฟเวอร์…", spinning: true });
    if (offlineData.cacheError) {
      return withQueue({ text: "ข้อมูลล่าสุดแสดงอยู่ แต่บันทึกลงเครื่องไม่ได้ — ปิดแอพแล้วข้อมูลจะไม่อยู่", spinning: false });
    }
    if (offlineData.stale && offlineData.source !== "empty") {
      // date AND time: a snapshot from the start of the shift and one from minutes ago both render
      // as today, so a date alone reads as current to a crew at the end of a shift
      const at = offlineData.fetchedAt
        ? `${formatDisplayDate(offlineData.fetchedAt)} ${formatDisplayTime(offlineData.fetchedAt)}`.trim()
        : null;
      const saved = at ? `แสดงข้อมูลที่บันทึกไว้ (${at})` : "แสดงข้อมูลที่บันทึกไว้";
      // Only claim the device is offline when it is. A permission page, an HTTP 4xx or malformed
      // JSON all fail while online, and their diagnostic code belongs on screen.
      const offline = typeof navigator !== "undefined" && navigator.onLine === false;
      if (offline) return withQueue({ text: `ออฟไลน์ — ${saved}`, spinning: false });
      const code = offlineData.error && offlineData.error.code ? ` (${offlineData.error.code})` : "";
      return withQueue({ text: `เชื่อมต่อเซิร์ฟเวอร์ไม่ได้${code} — ${saved}`, spinning: false });
    }
    // nothing else to report: the queue's state stands on its own. "Saved" here means saved on this
    // device, and nothing else on screen tells the crew the difference.
    return queueNote ? { text: queueNote, spinning: false } : null;
  }, [rowsReady, offlineData.refreshing, offlineData.stale, offlineData.source, offlineData.fetchedAt, offlineData.cacheError, offlineData.error, syncSummary]);

  if (isLoadingMain) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-surface-page font-sans">
      <Loader2 className="animate-spin w-12 h-12 mb-5 text-navy" />
      <div className="font-black text-navy text-lg tracking-tight">Connecting to Server...</div>
      <p className="text-ink-3 text-sm mt-2 font-medium">กำลังเตรียมข้อมูล TBM System</p>
    </div>
  );

  return (
    <Shell
      statusButton={offlineControls.button}
      offlineOverlays={offlineControls.overlays}
      moreSheetFooter={offlineControls.installPanel}
      active={{ tab: activeTab, module: currentModule }}
      onNavigate={handleNavigate}
      title={pageTitle}
      liveStatus={shellLiveStatus}
      projectInfo={projectInfo}
      onProjectChange={handleProjectInfoChange}
      moreOpen={moreOpen}
      setMoreOpen={setMoreOpen}
      issues={activeIssues}
      onSaveIssue={handleSaveIssue}
      onSetIssueStatus={handleSetIssueStatus}
      onDeleteIssue={handleDeleteIssue}
      segmentRecords={activeSegments}
      groutRecords={activeGrouts}
      shiftReports={activeShiftReports}
      activeMachine={activeMachine}
      onMachineChange={setActiveMachine}
      currentRingNum={currentRingNum}
      globalFilter={dashFilter}
      isViewer={isViewer}
    >
      {loadError && <div className="mb-6 bg-code-d/10 border border-code-d/30 text-code-d p-4 rounded-card text-center no-print font-semibold">{loadError}</div>}
      {/* the queue's own state is shown even under `loadError` — that banner means the app could not
          load anything from the server, which is a fresh install on site with no signal, and it is
          precisely then that the crew is recording into a queue nobody has told them about */}
      {offlineNotice && (
        <div className="mb-4 flex items-center justify-center gap-2 bg-code-b/10 border border-code-b/30 text-code-b px-4 py-2 rounded-card text-[13px] no-print font-semibold">
          {offlineNotice.spinning ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertCircle className="w-4 h-4" />}
          <span>{offlineNotice.text}</span>
        </div>
      )}
      {activeTab === "overview" && <OverviewView segmentRecords={activeSegments} groutRecords={activeGrouts} setCurrentModule={setCurrentModule} setActiveTab={setActiveTab} activeMachine={activeMachine} onMachineChange={setActiveMachine} />}
      {activeTab === "record" && currentModule === "grout" && <GroutRecordView projectInfo={projectInfo} handleProjectInfoChange={handleProjectInfoChange} groutRecords={activeGrouts} secondaryGroutRecords={activeSecondaryGrouts} segmentRecords={activeSegments} setCurrentModule={setCurrentModule} setActiveTab={setActiveTab} machine={activeMachine} isCurrentMachine={isCurrentMachine} onMutate={mutateBusinessRecord} syncMeta={syncMeta} />}
      {activeTab === "record" && currentModule === "segment" && <SegmentRecordView projectInfo={projectInfo} handleProjectInfoChange={handleProjectInfoChange} segmentRecords={activeSegments} setCurrentModule={setCurrentModule} setActiveTab={setActiveTab} machine={activeMachine} isCurrentMachine={isCurrentMachine} onMutate={mutateBusinessRecord} syncMeta={syncMeta} />}
      {activeTab === "dashboard" && <ExecutiveDashboardView segmentRecords={activeSegments} groutRecords={activeGrouts} shiftReports={activeShiftReports} dailyReports={activeDailyReports} machine={activeMachine} onNavigate={handleNavigate} filterState={dashFilter.state} readOnly={isViewer} distPlanConfig={distPlanConfigs[activeMachine] ?? null} prepTasks={activePrepTasks} onMutate={mutateBusinessRecord} syncMeta={syncMeta} />}
      {activeTab === "analysis" && currentModule === "segment" && <SegmentAnalysisView segmentRecords={activeSegments} projectInfo={projectInfo} machine={activeMachine} filterState={dashFilter.state} readOnly={isViewer} onMutate={mutateBusinessRecord} syncMeta={syncMeta} planConfig={planConfigs[activeMachine] ?? null} />}
      {activeTab === "analysis" && currentModule === "grout" && <GroutAnalysisView groutRecords={activeGrouts} secondaryGroutRecords={activeSecondaryGrouts} readOnly={isViewer} />}
      {activeTab === "analysis" && currentModule === "route" && <RouteScheduleView segmentRecords={activeSegments} projectInfo={projectInfo} machine={activeMachine} machineProgress={machineProgress} routeProjectTotal={routeProjectTotal} filterState={dashFilter.state} readOnly={isViewer} onMutate={mutateBusinessRecord} syncMeta={syncMeta}  routeConfigs={routeConfigs} distPlanConfig={distPlanConfigs[activeMachine] ?? null} />}
      {activeTab === "head_level" && <HeadLevelView segmentRecords={activeSegments} machine={activeMachine} readOnly={isViewer} />}
      {activeTab === "performance" && <PerformanceView segmentRecords={activeSegments} shiftReports={activeShiftReports} filterState={dashFilter.state} />}
      {activeTab === "prep_gantt" && <PrepGanttView machine={activeMachine} readOnly={isViewer} onMutate={mutateBusinessRecord} syncMeta={syncMeta} tasks={activePrepTasks} />}
      {activeTab === "datalog" && currentModule === "grout" && <GroutDashboardView groutRecords={activeGrouts} secondaryGroutRecords={activeSecondaryGrouts} segmentRecords={activeSegments} machine={activeMachine} onMutate={mutateBusinessRecord} syncMeta={syncMeta} readOnly={isViewer} />}
      {activeTab === "datalog" && currentModule === "segment" && <SegmentDashboardView segmentRecords={activeSegments} machine={activeMachine} onMutate={mutateBusinessRecord} syncMeta={syncMeta} planConfig={planConfigs[activeMachine] ?? null} />}
      {activeTab === "report" && <ReportView segmentRecords={activeSegments} groutRecords={activeGrouts} projectInfo={projectInfo} shiftReports={activeShiftReports} machine={activeMachine} onCreateDaily={(draft) => { setPendingRecordForm(draft); setActiveTab("record_daily"); }} />}
      {activeTab === "shift_report" && <ShiftReportView projectInfo={projectInfo} segmentRecords={activeSegments} shiftReports={activeShiftReports} machine={activeMachine} isCurrentMachine={isCurrentMachine} onMutate={mutateBusinessRecord} syncMeta={syncMeta} readOnly={isViewer} />}
      {activeTab === "record_daily" && <RecordDailyView dailyReports={activeDailyReports} onSave={(form) => { handleSaveDailyReport(form); setActiveTab("daily_report"); }} pendingForm={pendingRecordForm} onConsumePendingForm={() => setPendingRecordForm(null)} activeMachine={activeMachine} />}
      {activeTab === "daily_report" && <DailyReportView dailyReports={activeDailyReports} onDelete={handleDeleteDailyReport} onEdit={(formReady) => { setPendingRecordForm(formReady); setActiveTab("record_daily"); }} onGoRecord={() => setActiveTab("record_daily")} />}
      {activeTab === "inst_dashboard" && (
        <InstrumentDashboardView
          locations={instLocations} instruments={instInstruments} schedules={instSchedules}
          machineProgress={machineProgress} activeMachine={activeMachine}
          onOpenLocation={(id) => { setSelectedInstLocId(id); setActiveTab("inst_location"); }}
          onMark={isViewer ? null : handleMarkInstSchedule}
          readOnly={isViewer} />
      )}
      {activeTab === "inst_location" && (
        <InstrumentLocationView
          location={instLocations.find((l) => String(l.id) === String(selectedInstLocId)) || null}
          instruments={instInstruments.filter((i) => String(i.locationId) === String(selectedInstLocId))}
          allInstruments={instInstruments}
          readings={instReadings} thresholds={instThresholds}
          schedules={instSchedules} machineProgress={machineProgress} activeMachine={activeMachine}
          onMark={isViewer ? null : handleMarkInstSchedule}
          onBack={() => setActiveTab("inst_dashboard")} readOnly={isViewer} />
      )}
      {activeTab === "inst_schedule" && (
        <InstrumentScheduleView
          schedules={instSchedules} locations={instLocations} machineProgress={machineProgress} activeMachine={activeMachine}
          onMark={isViewer ? null : handleMarkInstSchedule} readOnly={isViewer} />
      )}
    </Shell>
  );
};

export default PrimaryGroutApp;
